// useDockRecorder — MediaRecorder capture for the voice-dock.
//
// On top of the plain recorder it exposes a live AnalyserNode so the dock can
// draw a real-time waveform. Speech-to-text still happens server-side — this
// hook only produces an audio Blob and the amplitude signal.

import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

export type DockRecorderState = 'idle' | 'requesting' | 'recording' | 'processing';

export interface DockRecording {
  readonly blob: Blob;
  readonly filename: string;
}

export interface UseDockRecorder {
  readonly state: DockRecorderState;
  readonly error: string | null;
  /** Live analyser of the mic stream while recording; null otherwise. */
  readonly analyser: AnalyserNode | null;
  /** Starts capture. Resolves false if the mic is unavailable/denied. */
  start(): Promise<boolean>;
  /** Stops capture. Resolves null when the clip is too short to send. */
  stop(): Promise<DockRecording | null>;
  /** Clears the error and returns to idle without touching the stream. */
  reset(): void;
}

const MIN_BLOB_BYTES = 1000;
/**
 * Минимальная длительность записи (мс). Короче — считаем случайным тапом
 * «запись → сразу стоп»: контейнерные заголовки webm могут превысить
 * MIN_BLOB_BYTES, поэтому проверка по размеру blob недостаточна (aidepo-4).
 */
const MIN_DURATION_MS = 800;

/** First usable MIME type from the preference list, or '' (browser default). */
function pickMimeType(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', ''];
  for (const m of candidates) {
    if (m === '' || MediaRecorder.isTypeSupported(m)) return m;
  }
  return '';
}

/** Derives a filename for the recorded blob from its MIME type. */
function filenameForMime(mimeType: string): string {
  const ext = mimeType.includes('mp4') ? 'mp4' : mimeType.includes('webm') ? 'webm' : 'wav';
  return `msg.${ext}`;
}

export function useDockRecorder(): UseDockRecorder {
  const [state, setState] = useState<DockRecorderState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const mimeRef = useRef<string>('');
  const audioCtxRef = useRef<AudioContext | null>(null);
  const startedAtRef = useRef<number>(0);

  /** Stops the stream, closes the audio context, drops references. */
  const releaseStream = useCallback((): void => {
    const stream = streamRef.current;
    if (stream !== null) {
      for (const track of stream.getTracks()) track.stop();
    }
    streamRef.current = null;
    recorderRef.current = null;
    const ctx = audioCtxRef.current;
    if (ctx !== null && ctx.state !== 'closed') void ctx.close();
    audioCtxRef.current = null;
    setAnalyser(null);
  }, []);

  // Clean up if the component unmounts mid-recording.
  useEffect(() => releaseStream, [releaseStream]);

  const start = useCallback(async (): Promise<boolean> => {
    setError(null);
    setState('requesting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = pickMimeType();
      mimeRef.current = mimeType;
      const recorder =
        mimeType === '' ? new MediaRecorder(stream) : new MediaRecorder(stream, { mimeType });
      recorderRef.current = recorder;

      chunksRef.current = [];
      recorder.addEventListener('dataavailable', (e: BlobEvent) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      });

      // Analyser tap for the live waveform — same stream, separate consumer.
      const ctx = new AudioContext();
      audioCtxRef.current = ctx;
      if (ctx.state === 'suspended') await ctx.resume();
      const node = ctx.createAnalyser();
      node.fftSize = 1024;
      ctx.createMediaStreamSource(stream).connect(node);
      setAnalyser(node);

      recorder.start();
      startedAtRef.current = Date.now();
      setState('recording');
      return true;
    } catch {
      releaseStream();
      setState('idle');
      setError('Нет доступа к микрофону. Разреши доступ в настройках браузера и попробуй снова.');
      return false;
    }
  }, [releaseStream]);

  const stop = useCallback((): Promise<DockRecording | null> => {
    const recorder = recorderRef.current;
    if (recorder === null || recorder.state === 'inactive') {
      releaseStream();
      setState('idle');
      return Promise.resolve(null);
    }

    setState('processing');

    return new Promise<DockRecording | null>((resolve) => {
      recorder.addEventListener(
        'stop',
        () => {
          const type = mimeRef.current.length > 0 ? mimeRef.current : 'audio/webm';
          const blob = new Blob(chunksRef.current, { type });
          chunksRef.current = [];
          releaseStream();

          // Слишком короткую запись на API не отправляем — мягкая подсказка
          // вместо ошибки распознавания (aidepo-4). Гейт по длительности И по
          // размеру: каждый ловит свой случай.
          const elapsedMs = Date.now() - startedAtRef.current;
          if (elapsedMs < MIN_DURATION_MS || blob.size < MIN_BLOB_BYTES) {
            setState('idle');
            setError('Слишком коротко — скажи ещё раз, чуть подольше.');
            resolve(null);
            return;
          }

          setState('idle');
          resolve({ blob, filename: filenameForMime(type) });
        },
        { once: true },
      );
      recorder.stop();
    });
  }, [releaseStream]);

  const reset = useCallback((): void => {
    setError(null);
    setState('idle');
  }, []);

  return { state, error, analyser, start, stop, reset };
}
